import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { LogOut, Mail, Phone, MessageSquare, Calendar, User } from 'lucide-react';

const AdminDashboard = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

useEffect(() => {
  (async () => {
    await fetchMessages();
  })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const fetchMessages = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      } else {
        throw new Error('Failed to fetch messages');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch contact messages",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast({
      title: "Logged Out",
      description: "You have been logged out successfully",
    });
    navigate('/admin/login');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const logoUrl = 'https://customer-assets.emergentagent.com/job_jyoti-agency/artifacts/299sy1av_Logo%20%282%29.png';

  return (
    <div className="min-h-screen hero-section">
      {/* Header */}
      <div className="bg-white shadow-lg border-b-4 border-yellow-500">
        <div className="max-w-7xl mx-auto px-4 py-6 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img src={logoUrl} alt="Jyoti Web Design" className="h-16" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">Welcome, {user?.name || 'Admin'}</p>
            </div>
          </div>
          <div className="flex gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-2 border-gray-900 text-gray-900 hover:bg-gray-100"
            >
              View Website
            </Button>
            <Button
              onClick={handleLogout}
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-white rounded-2xl shadow-xl border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-lg">Total Messages</p>
                  <p className="text-4xl font-bold text-gray-900 mt-2">{messages.length}</p>
                </div>
                <MessageSquare className="w-12 h-12 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-2xl shadow-xl border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-lg">New Messages</p>
                  <p className="text-4xl font-bold text-gray-900 mt-2">
                    {messages.filter(m => m.status === 'new').length}
                  </p>
                </div>
                <Mail className="w-12 h-12 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-2xl shadow-xl border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-lg">This Month</p>
                  <p className="text-4xl font-bold text-gray-900 mt-2">
                    {messages.filter(m => {
                      const msgDate = new Date(m.created_at);
                      const now = new Date();
                      return msgDate.getMonth() === now.getMonth() && msgDate.getFullYear() === now.getFullYear();
                    }).length}
                  </p>
                </div>
                <Calendar className="w-12 h-12 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Messages Table */}
        <Card className="bg-white rounded-2xl shadow-xl border-0">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-gray-900">Contact Messages</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-gray-600">Loading messages...</p>
            ) : messages.length === 0 ? (
              <p className="text-center py-8 text-gray-600">No messages yet</p>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <Card key={message.id} className="bg-gray-50 border-2 border-gray-200 hover:border-yellow-500 transition-colors duration-200">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <User className="w-10 h-10 text-yellow-600" />
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">{message.name}</h3>
                            <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(message.created_at)}
                            </p>
                          </div>
                        </div>
                        <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                          message.status === 'new' ? 'bg-yellow-500 text-white' : 'bg-gray-300 text-gray-700'
                        }`}>
                          {message.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="space-y-2 mb-4">
                        <p className="text-gray-700 flex items-center gap-2">
                          <Mail className="w-5 h-5 text-yellow-600" />
                          <a href={`mailto:${message.email}`} className="hover:text-yellow-600 transition-colors">
                            {message.email}
                          </a>
                        </p>
                        <p className="text-gray-700 flex items-center gap-2">
                          <Phone className="w-5 h-5 text-yellow-600" />
                          <a href={`tel:${message.phone}`} className="hover:text-yellow-600 transition-colors">
                            {message.phone}
                          </a>
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                        <p className="text-lg font-semibold text-gray-900 mb-2">Message:</p>
                        <p className="text-gray-700 leading-relaxed">{message.message}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
